import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest
} from '@angular/common/http';
import { catchError, finalize, Observable, tap } from 'rxjs';
import { ApplicationRef, inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { REQUEST, RESPONSE } from '../../DI';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Keys within cached response data structure.
 */

export const BODY = 'b';
export const HEADERS = 'h';
export const STATUS = 's';
export const STATUS_TEXT = 'st';
export const REQ_URL = 'u';
export const RESPONSE_TYPE = 'rt';
interface TransferHttpResponse {
  /** body */
  [BODY]: any;
  /** headers */
  [HEADERS]: Record<string, string[]>;
  /** status */
  [STATUS]?: number;
  /** statusText */
  [STATUS_TEXT]?: string;
  /** url */
  [REQ_URL]?: string;
  /** responseType */
  [RESPONSE_TYPE]?: HttpRequest<unknown>['responseType'];
}
export function loggingInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  console.log('httpclient: ' + req.url);
  console.log('req headers after: ', req.headers);
  return next(req);
}

export function SSRPerformanceInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformServer(platformId)) {
    return next(req);
  }

  const serverRequest = inject(REQUEST);
  const serverResponse = inject(RESPONSE);
  const requestId = serverRequest.get('x-request-id');
  const startedAt = performance.now();
  let status = 0;
  const requestWithId = requestId
    ? req.clone({setHeaders: {'X-Request-ID': requestId}})
    : req;

  return next(requestWithId).pipe(
    tap({
      next: event => {
        if (event.type === HttpEventType.Response || event.type === HttpEventType.ResponseHeader) {
          status = event.status;
        }
      },
      error: error => {
        if (error instanceof HttpErrorResponse) {
          status = error.status;
        }
      }
    }),
    finalize(() => {
      const durationMs = performance.now() - startedAt;
      if (!serverResponse.headersSent) {
        serverResponse.append('Server-Timing', `ssr_api;dur=${durationMs.toFixed(1)}`);
      }
      console.info(JSON.stringify({
        timestamp: new Date().toISOString(),
        event: 'ssr.http',
        requestId: requestId || null,
        method: req.method,
        path: getRequestPath(req.urlWithParams),
        status,
        durationMs: Number(durationMs.toFixed(1))
      }));
    })
  );
}

export function SSRCookieInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);
  if (isPlatformServer(platformId)) {
    // console.log('req headers before: ', req.headers);
    if (req.headers.has('cookie')) {
      // console.log('cookie is not none: ',  req.headers.get('cookie'));
      return next(req);
    }
    const serverRequest = inject(REQUEST);
    let headers = req.headers.set('Cookie', String(serverRequest.get('cookie')))
      .set('Accept', 'application/json')
      .set('Accept-Encoding', 'gzip, deflate, br, zstd')
      .set('Accept-Language', 'en-US,en;q=0.5')
      .set('Cache-Control', 'no-cache');
      if (!req.headers.has('origin')) {
        headers = headers.set('Origin', 'http://127.0.0.1:3000');
      }
      if (!req.headers.has('referer')) {
        headers = headers.set('Referer', 'http://127.0.0.1:3000/');
      }
      if (serverRequest.get('user-agent') && !req.headers.has('user-agent')) {
        headers = headers.set('User-Agent', String(serverRequest.get('user-agent')));
      }
      headers = headers.set('Sec-Fetch-Dest', 'empty').set('Sec-Fetch-Mode', 'cors').set('Sec-Fetch-Site', 'same-site');

    const requestWithCookies = req.clone({
      headers: headers
    });
    return next(requestWithCookies);
  }
  return next(req);
}

export function SSRSetCookieForResponseInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);
  let serverResponse = null;
  if (isPlatformServer(platformId)) {
    serverResponse = inject(RESPONSE);
  }
  return next(req).pipe(tap(event => {
    // console.log(event.type, (event as any).status);
    if (event.type === HttpEventType.Response || event.type ===  HttpEventType.ResponseHeader) {
      if (isPlatformServer(platformId) && serverResponse) {
        const serverCookies = event.headers.get('set-cookie');
        if (serverCookies) {
          serverResponse.set('set-cookie', serverCookies);
        }
      }
    }
    // if (event.type === HttpEventType.Response) {
      // console.log('response header: ', event.headers);
      // console.log('response body: ', event.body);
    // }
  }));
}

export function OryFailedRequestCacheInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);
  const appRef = inject(ApplicationRef);
  const transferState = inject(TransferState);
  if (isStable(appRef)) {
    return next(req);
  }
  const stateKey = makeStateKey<TransferHttpResponse>(`(err)(${req.method})${req.url}`);
  // console.log(stateKey);
  const response = transferState.get(stateKey, null);
  if (response) {
    // console.log(response);
    const {
      [BODY]: undecodedBody,
      [RESPONSE_TYPE]: responseType,
      [HEADERS]: httpHeaders,
      [STATUS]: status,
      [STATUS_TEXT]: statusText,
      [REQ_URL]: url,
    } = response;

    throw new HttpErrorResponse({
      error: undecodedBody,
      headers: new HttpHeaders(httpHeaders),
      status: status,
      statusText: statusText,
      url: url
    });
  }
  return next(req).pipe(catchError((err, caught) => {
    // console.log(err);
    if (isPlatformServer(platformId) && err.status > 399 && err.status < 500) {
      // console.log('in server and 400');
      transferState.set<TransferHttpResponse>(stateKey, {
        [BODY]: err.error,
        [RESPONSE_TYPE]: err.type,
        [HEADERS]: err.headers,
        [STATUS]: err.status,
        [STATUS_TEXT]: err.statusText,
        [REQ_URL]: err.url,
      });
    }
    throw err;
  }));
}

function isStable(appRef: ApplicationRef): boolean {
  return toSignal(appRef.isStable, {requireSync: true})();
}

function getRequestPath(requestUrl: string): string {
  try {
    return new URL(requestUrl, 'https://box.moe').pathname;
  } catch {
    return '<invalid-url>';
  }
}
